import { useState } from 'react';
import styled from 'styled-components';
import { FiPhone, FiMail, FiMapPin } from 'react-icons/fi';
import useScrollReveal from '../hooks/useScrollReveal';
import WaveDivider from '../components/WaveDivider';
import PageHero from '../components/PageHero';

const ContactSection = styled.section`
  background: var(--cream);
  padding: 100px 24px;
`;

const ContactContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const InfoColumn = styled.div``;

const InfoHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 12px;
`;

const InfoSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-body);
  line-height: 1.7;
  margin-bottom: 32px;
`;

const ContactItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  padding: 16px 0;
`;

const ContactIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--sage);
  font-size: 1.3rem;
  flex-shrink: 0;
`;

const ContactText = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: var(--text-body);
  line-height: 1.6;
`;

const ContactLink = styled.a`
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: var(--text-body);
  transition: color 0.3s ease;

  &:hover {
    color: var(--spruce);
  }
`;

const LicenseNote = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  color: var(--text-muted);
  margin-top: 24px;
`;

const FormCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 1px 4px var(--shadow);
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 0.85rem;
  color: var(--text-dark);
  margin-bottom: 6px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 14px 18px;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: var(--text-dark);
  background: white;
  transition: border-color 0.3s ease;
  box-sizing: border-box;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--sage);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 14px 18px;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: var(--text-dark);
  background: white;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--sage);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 14px 18px;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: var(--text-dark);
  background: white;
  transition: border-color 0.3s ease;
  resize: vertical;
  box-sizing: border-box;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--sage);
  }
`;

const SubmitButton = styled.button`
  display: inline-block;
  background: var(--sage);
  color: white;
  padding: 14px 36px;
  border-radius: 4px;
  border: none;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.3s ease;
  width: 100%;

  &:hover {
    background: var(--sage-muted);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const SuccessHeading = styled.h3`
  font-family: 'Lora', serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--spruce);
  margin-bottom: 16px;
`;

const SuccessText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-body);
  line-height: 1.7;
`;

const ErrorText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: #b3261e;
  margin-top: -8px;
  margin-bottom: 20px;
`;

const ContactNatureImage = styled.img`
  width: 100%;
  border-radius: 8px;
  object-fit: cover;
  max-height: 250px;
  margin-top: 32px;
  display: block;
`;

const HIPAANote = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  color: var(--text-muted);
  text-align: center;
  padding-top: 40px;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.7;
`;

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mwvgnlzz';

const Contact = () => {
  useScrollReveal();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Inquiry',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(false);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        label="Contact"
        title="Get in Touch"
        subtitle="I'd love to hear from you. Take the first step today."
      />

      <WaveDivider topColor="#e4e8e1" bottomColor="#eef0eb" />

      <ContactSection>
        <ContactContainer className="fade-in">
          <ContactGrid>
            <InfoColumn>
              <InfoHeading>Let's Connect</InfoHeading>
              <InfoSubtitle>
                Book a free 15-minute phone consultation, or reach out via email.
              </InfoSubtitle>
              <ContactItem>
                <ContactIcon>
                  <FiPhone />
                </ContactIcon>
                <ContactLink href="tel:8022558083">(802) 255-8083</ContactLink>
              </ContactItem>
              <ContactItem>
                <ContactIcon>
                  <FiMail />
                </ContactIcon>
                <ContactLink href="mailto:audreykielylicsw@gmail.com">
                  audreykielylicsw@gmail.com
                </ContactLink>
              </ContactItem>
              <ContactItem>
                <ContactIcon>
                  <FiMapPin />
                </ContactIcon>
                <ContactText>
                  In person in Northern Vermont
                  <br />
                  Telehealth in VT, MA, CA &amp; PA
                </ContactText>
              </ContactItem>
              <LicenseNote>
                Licensed in Vermont, Massachusetts, California, and Pennsylvania
              </LicenseNote>
              <ContactNatureImage
                src="/images/mountain-view.jpg"
                alt="Vermont Green Mountains at sunrise"
              />
            </InfoColumn>

            <FormCard>
              {submitted ? (
                <SuccessMessage>
                  <SuccessHeading>Thank You</SuccessHeading>
                  <SuccessText>
                    Thank you for reaching out! I'll get back to you within 48
                    hours.
                  </SuccessText>
                </SuccessMessage>
              ) : (
                <form onSubmit={handleSubmit}>
                  <FormGroup>
                    <FormLabel htmlFor="name">Name</FormLabel>
                    <FormInput
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel htmlFor="email">Email</FormLabel>
                    <FormInput
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel htmlFor="phone">Phone</FormLabel>
                    <FormInput
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="Phone Number (optional)"
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel htmlFor="subject">Subject</FormLabel>
                    <FormSelect
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                    >
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Schedule Consultation">
                        Schedule Consultation
                      </option>
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel htmlFor="message">Message</FormLabel>
                    <FormTextarea
                      id="message"
                      name="message"
                      placeholder="Tell me a little about what brings you here..."
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>
                  {error && (
                    <ErrorText>
                      Something went wrong sending your message. Please try
                      again, or reach out by phone or email above.
                    </ErrorText>
                  )}
                  <SubmitButton type="submit" disabled={submitting}>
                    {submitting ? 'Sending...' : 'Send Message'}
                  </SubmitButton>
                </form>
              )}
            </FormCard>
          </ContactGrid>

          <HIPAANote>
            Please note: This contact form is not considered secure. Please
            limit confidential information included in your outreach. This form
            is not a guarantee of treatment, and is not monitored 24/7. If you
            are experiencing an emergency or crisis, please seek emergency
            support.
          </HIPAANote>
        </ContactContainer>
      </ContactSection>

      <WaveDivider topColor="#eef0eb" bottomColor="#1a2e1a" />
    </>
  );
};

export default Contact;
